import { defineRouting } from "next-intl/routing";

export const routing = defineRouting({
  locales: ["nl", "en"],
  defaultLocale: "nl",
  // NL lives at "/", English at "/en" - matching the original site structure.
  // Path segments are shared across locales so canonical + hreflang URLs stay
  // consistent (English pages are simply prefixed with /en).
  localePrefix: "as-needed",
  // The URL decides the language, nothing else.
  //
  // next-intl defaults this to true, which made the middleware 307 anyone whose
  // browser prefers English from "/" to "/en". That is wrong for this site on
  // three counts:
  //
  //   1. This is a Dutch service for Dutch NS passengers. A traveller in
  //      Utrecht with an English-language phone - extremely common - was being
  //      sent to the English page for a product sold in Dutch.
  //   2. It put a redirect hop in front of the LCP of every cold paid landing.
  //   3. It broke the A/B test. The Framer control arm has no English version
  //      at all, so the two arms were not serving the same page to the same
  //      people, and the difference had nothing to do with the redesign.
  //
  // Turning it off disables the Accept-Language sniff AND the NEXT_LOCALE
  // cookie. A locale PREFIX in the path still wins over everything (see
  // resolveLocale in next-intl), so /en keeps serving English and the language
  // switcher keeps working - it navigates by URL, not by cookie.
  localeDetection: false,
  // Do not emit the `Link: rel="alternate" hreflang` response header.
  //
  // next-intl builds that header from the REQUEST host, and CloudFront reaches
  // the origin through an origin request policy that strips `host` (it has to:
  // forwarding host or authorization breaks the OAC SigV4 signature on the
  // Lambda origin). So the origin only ever sees its own API Gateway hostname,
  // and every page on the apex advertised hreflang pointing at
  // `zj8wierc9a.execute-api.eu-west-1.amazonaws.com`. Google reads hreflang from
  // HTTP headers, so that header actively contradicted the page's own tags.
  //
  // The header is redundant anyway: generateMetadata emits the real
  // <link rel="alternate" hreflang> tags, built from the hard-coded apex in
  // site.domain, so they are correct no matter which host served the request.
  alternateLinks: false,
});

// Static (non-dynamic) routes used for sitemap generation.
export const staticRoutes = [
  "/",
  "/blog",
  "/ken-je-rechten",
  "/over-ons",
  "/vacatures",
  "/klantenservice",
  "/veelgestelde-vragen",
  "/tarieven",
  "/ns-staking",
  "/privacybeleid",
  "/cookiebeleid",
  "/zakelijk-privacybeleid",
  "/algemene-voorwaarden",
] as const;

export type Locale = (typeof routing.locales)[number];
